import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSync } from '../lib/SyncProvider';
import { CheckIcon, CloseIcon, RefreshIcon } from './Icons';

function statusLabel(status: string): string {
  if (status === 'syncing') return '正在同步…';
  if (status === 'synced') return '已同步';
  if (status === 'error') return '同步失败';
  if (status === 'idle') return '等待同步';
  return '未连接';
}

export function IdentityDialog() {
  const { connected, status, error, verifying, lastSyncedAt, dialogOpen, closeDialog, verifyCode, syncNow } = useSync();
  const [code, setCode] = useState('');
  const [localError, setLocalError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!dialogOpen) return;
    setLocalError('');
    setCode('');
    const timer = window.setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDialog();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [dialogOpen, closeDialog]);

  useEffect(() => {
    if (dialogOpen && !connected && error) setLocalError(error);
  }, [dialogOpen, connected, error]);

  if (!dialogOpen) return null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setLocalError('');
    if (!/^\d{6}$/.test(code)) {
      setLocalError('请输入 6 位数字验证码。');
      return;
    }
    const ok = await verifyCode(code);
    if (!ok) setLocalError(error || '验证码不可用，请重试。');
  };

  return (
    <div className="identity-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeDialog()}>
      <section className="identity-dialog" role="dialog" aria-modal="true" aria-labelledby="identity-title">
        <div className="identity-dialog-head">
          <span className="eyebrow">PERSONAL IDENTITY / SYNC</span>
          <button className="icon-button" type="button" onClick={closeDialog} aria-label="关闭个人身份">
            <CloseIcon size={17} />
          </button>
        </div>

        {connected ? (
          <>
            <div className="identity-connected-mark">
              <CheckIcon size={19} />
            </div>
            <h2 id="identity-title">个人身份已连接<span>。</span></h2>
            <p className="identity-copy">
              这台设备会记住验证码。已看、收藏、最近浏览、笔记和路径进度会与使用同一验证码的设备同步。
            </p>
            <div className="identity-status-line">
              <span className={status === 'error' ? 'identity-status is-error' : 'identity-status'}>{statusLabel(status)}</span>
              {lastSyncedAt ? <time dateTime={lastSyncedAt}>最近同步：{new Date(lastSyncedAt).toLocaleString('zh-CN', { hour12: false })}</time> : null}
            </div>
            {error ? <p className="identity-error" role="alert">{error}</p> : null}
            <div className="identity-actions">
              <button className="button button-secondary" type="button" onClick={() => void syncNow()} disabled={status === 'syncing'}>
                <RefreshIcon size={15} /> 立即同步
              </button>
              <button className="button button-primary" type="button" onClick={closeDialog}>
                关闭
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="identity-title">输入验证码<span>。</span></h2>
            <p className="identity-copy">
              只用于识别你的个人数据，不阻止任何人浏览网站。验证成功后，这台设备会记住身份；其他设备输入同一个验证码即可同步。
            </p>
            <form className="identity-form" onSubmit={(event) => void submit(event)}>
              <label htmlFor="personal-code">6 位验证码</label>
              <input
                ref={inputRef}
                id="personal-code"
                type="password"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="••••••"
                aria-describedby="identity-error"
              />
              {localError || error ? <p className="identity-error" id="identity-error" role="alert">{localError || error}</p> : null}
              <button className="button button-primary identity-submit" type="submit" disabled={verifying || code.length !== 6}>
                {verifying ? '验证中…' : '确认身份'}
              </button>
            </form>
            <p className="identity-caption">验证码保存在本机，不存账号密码；清除浏览器数据后需要重新输入。</p>
          </>
        )}
      </section>
    </div>
  );
}
